import { styled } from '@mui/material/styles';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import type { ChangeEvent, CSSProperties, ReactNode, Ref } from 'react';
import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react';
import { sizes } from '../../theme/tokens';
import { IconButton } from '../Button';
import type { TextFieldProps } from '../TextField';
import { InputAdornment, TextField } from '../TextField';
import { Tooltip } from '../Tooltip';

export interface SearchFieldProps
  extends Omit<
    TextFieldProps,
    | 'value'
    | 'defaultValue'
    | 'onChange'
    | 'placeholder'
    | 'type'
    | 'multiline'
    | 'rows'
    | 'minRows'
    | 'maxRows'
    | 'select'
    | 'children'
    | 'inputRef'
  > {
  /**
   * The call to action, e.g. "Search Deal Name or ID". Required: by default there is no label,
   * so this is the only thing that tells a reader what the field searches. It also becomes the
   * field's accessible name unless `label` or `inputLabel` is given.
   */
  placeholder: string;
  /** Controlled value. Leave it out for an uncontrolled field (`defaultValue` seeds it). */
  value?: string;
  defaultValue?: string;
  /**
   * `TextField`'s own signature, so a search box replaces a `TextField` without touching the
   * handler. Fires for typing AND for a clear (✕ or Escape), with a real event whose `target` is
   * the input — a clear is just a change to `''`.
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * The query to actually search for, debounced (`debounceMs`) and trimmed. Emptying the field,
   * or dropping below `minLength`, is reported **immediately** — a reader who clears a search is
   * asking to see everything again. Enter flushes a pending query at once. Leave it out when the
   * screen filters on every keystroke itself (`onChange` is enough then).
   */
  onSearch?: (query: string) => void;
  /** Pause after the last keystroke before `onSearch` fires. Default 300ms, like the filter panel. */
  debounceMs?: number;
  /** Characters required before `onSearch` is given a real query. Default 0. */
  minLength?: number;
  /**
   * Shown in a tooltip under the field while the typed text is shorter than `minLength` — a
   * tooltip, not helper text, so the field never changes height as the user types. Also read to
   * assistive tech as the field's description.
   */
  hint?: ReactNode;
  /** Called after the field was cleared by its ✕ or by Escape. `onChange` has already fired. */
  onClear?: () => void;
  /** Accessible name of the ✕. Default "Clear search"; override to localize. */
  clearLabel?: string;
  /**
   * Accessible name of the input when there is no `label`. Defaults to the placeholder; give a
   * more specific one when the placeholder is generic ("Search" → "Search deals").
   */
  inputLabel?: string;
  /** Ref to the native `<input>`. */
  inputRef?: Ref<HTMLInputElement>;
}

/** Screen-reader-only (same recipe as GeoMap's region list): present in the tree, not on screen. */
const visuallyHidden: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * The layout root. `inline-flex` by default so the field is as wide as its content, like a plain
 * `TextField`; `fullWidth` makes it a block that fills its container. A width on `sx` lands here,
 * and the field inside ALWAYS fills this root — which is what removes the `TextField` trap where
 * `sx={{ width }}` sized the outer box while the field kept its intrinsic width and the leftover
 * read as a mysterious gap (FilterBar, 2026-09-22).
 */
const StyledRoot = styled('div', { shouldForwardProp: prop => prop !== 'fullWidth' })<{
  fullWidth?: boolean;
}>(({ fullWidth }) => ({
  display: fullWidth ? 'flex' : 'inline-flex',
  flexDirection: 'column',
  minWidth: 0,
  ...(fullWidth && { width: '100%' }),
}));

/**
 * SearchField — the search box, wherever one is needed: the bar above a list, a panel, a dialog,
 * a side selector. Visually it IS the design system's `TextField` (same tokens, same `h36`
 * shell, same label tab when asked for); what it adds is everything a search box kept getting
 * wrong when hand-rolled from one.
 *
 * - **No label by default.** The placeholder is the call to action — "Search Deal Name or ID" —
 *   and it is also the accessible name, so the field is never an unlabelled textbox. Pass `label`
 *   and the brand label tab renders above the field exactly as on `TextField`.
 * - **A magnifier in front, a ✕ behind once there is text — in a slot reserved from the start.**
 *   The ✕ is always laid out and only shown (and focusable) while there is something to clear,
 *   so the field never changes width when it appears (designer, 2026-10-05); a disabled field
 *   never shows it. It clears the field and keeps focus in it: clearing is a step in searching,
 *   not the end of it, and a button that vanishes under focus drops a keyboard user to the top
 *   of the document. Three templates copied the markup of this pattern and all three lost that
 *   one line (review of PR #22) — hence a component.
 * - **Enter searches now, Escape clears.** With `onSearch`, Enter flushes a pending debounce;
 *   Escape empties a non-empty field and stops there, so a dialog around it does not also close
 *   on the same press.
 * - **Optional `onSearch`** with the filter panel's rules: debounced, trimmed, clearing immediate,
 *   `minLength` + a `hint` tooltip that does not change the field's height.
 *
 * Composition — LOCAL: none. SHARED: `TextField` → `OutlinedInput` → `InputBase` (every text
 * entry in the system), `InputAdornment`, `IconButton` → `ButtonBase`, `Tooltip` → `Popper`. All
 * reused as they are: this component adds no theme override, so it cannot ripple.
 */
export const SearchField = forwardRef<HTMLDivElement, SearchFieldProps>(function SearchField(
  {
    placeholder,
    value,
    defaultValue = '',
    onChange,
    onSearch,
    debounceMs = 300,
    minLength = 0,
    hint,
    onClear,
    clearLabel = 'Clear search',
    inputLabel,
    inputRef: inputRefProp,
    label,
    id: idProp,
    helperText,
    disabled,
    fullWidth = false,
    className,
    sx,
    slotProps,
    onKeyDown,
    onFocus,
    onBlur,
    ...rest
  },
  ref
) {
  const [internal, setInternal] = useState(defaultValue);
  const isControlled = value !== undefined;
  const text = isControlled ? value : internal;
  const hasText = text.length > 0;

  const inputRef = useRef<HTMLInputElement | null>(null);
  // Own ref + the consumer's, merged by hand. MUI's `useForkRef` would do it, but it lives on the
  // `@mui/material/utils` subpath that nothing else here imports — and a subpath the test
  // runner's optimizer meets for the first time mid-run reloads every suite (see
  // vitest.config.ts). Six lines beat another entry in that list.
  const handleInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof inputRefProp === 'function') inputRefProp(node);
      else if (inputRefProp) (inputRefProp as { current: HTMLInputElement | null }).current = node;
    },
    [inputRefProp]
  );

  // A fixed id so the helper text's id is known here: MUI derives it as `${id}-helper-text`, and
  // the input's `aria-describedby` below has to name it alongside the hint's.
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const hintId = `${id}-search-hint`;
  const helperTextId = helperText ? `${id}-helper-text` : undefined;

  const [focused, setFocused] = useState(false);

  /*
   * ---- onSearch: the filter panel's rules, in a field ----------------------------------------
   * The callback lives in a ref so the timer never captures a stale closure and a new handler
   * between renders does not restart the debounce.
   */
  const searchRef = useRef(onSearch);
  searchRef.current = onSearch;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPending = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: unmount-only cleanup; `cancelPending` reads a ref
  useEffect(() => cancelPending, []);

  const emitSearch = (raw: string) => {
    if (!searchRef.current) return;
    cancelPending();
    const query = raw.trim();
    // Emptying the field, or dropping below the threshold, resolves at once: there is no request
    // to spare, and the list has to stop showing answers to a query that is gone. The consumer
    // receives the short query and treats it as "show the default list" — the same contract as
    // FilterDropdownPanel's `onSearchChange`.
    if (query.length === 0 || query.length < minLength) {
      searchRef.current(query);
      return;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      searchRef.current?.(query);
    }, debounceMs);
  };

  const flushSearch = () => {
    if (!searchRef.current) return;
    cancelPending();
    searchRef.current(text.trim());
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) setInternal(event.target.value);
    onChange?.(event);
    emitSearch(event.target.value);
  };

  /**
   * Clear THROUGH the DOM rather than by calling `onChange` with a hand-made event: setting the
   * value with the prototype's setter (so React's value tracker still sees the old one) and
   * dispatching `input` makes React raise an ordinary change event whose `target` is the real
   * input. A consumer's `onChange={(e) => setQuery(e.target.value)}` therefore needs no special
   * case for the ✕, controlled or not — which is what lets a `TextField` be swapped for this
   * without touching the handler.
   */
  const clear = () => {
    const input = inputRef.current;
    if (!input) return;
    const setValue = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    setValue?.call(input, '');
    input.dispatchEvent(new Event('input', { bubbles: true }));
    // Now a no-op for React (its tracker already holds ''), but it goes through the INSTANCE
    // setter, which anything that wraps the element's `value` — test tooling such as user-event
    // keeps a per-element copy of what it typed — relies on to notice a programmatic change. Skip
    // it and the next simulated keystroke appends to text that is no longer there.
    input.value = '';
    // Focus belongs back in the field: clearing is a step in searching, not the end of it.
    input.focus();
    onClear?.();
  };

  const handleKeyDown: TextFieldProps['onKeyDown'] = event => {
    onKeyDown?.(event);
    // MUI delivers `onKeyDown` from the InputBase ROOT, so keys pressed on the ✕ inside the end
    // adornment arrive here too. Only the input's own keys are ours: an Enter on the ✕ has to
    // stay a native button activation, not a flushed search with its default prevented.
    if (event.defaultPrevented || event.target !== inputRef.current) return;
    if (event.key === 'Enter') {
      // Only when there is a search to flush. Without `onSearch` Enter stays native, so a search
      // box inside a <form> still submits it.
      if (searchRef.current) {
        event.preventDefault();
        flushSearch();
      }
    } else if (event.key === 'Escape' && hasText) {
      // Escape closes the innermost thing first: a non-empty field consumes it. An empty field
      // lets it through to whatever surrounds the search (a dialog, a drawer).
      event.preventDefault();
      event.stopPropagation();
      clear();
    }
  };

  const trimmedLength = text.trim().length;
  const belowThreshold = minLength > 0 && trimmedLength > 0 && trimmedLength < minLength;
  // Only once the user has started typing: a tooltip that pops the moment the field is focused
  // reads as an error before anything has been done.
  const hintOpen = Boolean(hint) && focused && belowThreshold;

  const consumerHtmlInput = (slotProps?.htmlInput ?? {}) as Record<string, unknown>;
  const consumerInput = (slotProps?.input ?? {}) as Record<string, unknown>;
  const readOnly = Boolean(consumerInput.readOnly || consumerHtmlInput.readOnly);
  // The ✕ is usable only while there is text to clear and the field can be edited. It is still
  // RENDERED otherwise (hidden, not unmounted) so its width is reserved and the field never
  // resizes as text comes and goes.
  const showClear = hasText && !disabled && !readOnly;
  const describedBy =
    [consumerHtmlInput['aria-describedby'], helperTextId, hint ? hintId : undefined].filter(Boolean).join(' ') ||
    undefined;

  const field = (
    <StyledRoot ref={ref} className={className} sx={sx} fullWidth={fullWidth}>
      <TextField
        {...rest}
        id={id}
        label={label}
        helperText={helperText}
        disabled={disabled}
        // Always: the field fills the Root, and the Root is what `sx` / `fullWidth` size.
        fullWidth
        placeholder={placeholder}
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={event => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={event => {
          setFocused(false);
          onBlur?.(event);
        }}
        inputRef={handleInputRef}
        slotProps={{
          ...slotProps,
          htmlInput: {
            // A search box is not a form field the browser should autofill, and mobile keyboards
            // get their "search" key.
            autoComplete: 'off',
            inputMode: 'search',
            enterKeyHint: 'search',
            ...consumerHtmlInput,
            // On the INPUT, never on the TextField root (where it would name nothing). With a
            // `label`, TextField links its tab through `aria-labelledby` instead.
            ...(label ? {} : { 'aria-label': inputLabel ?? placeholder }),
            'aria-describedby': describedBy,
          },
          input: {
            ...consumerInput,
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlass size={sizes.height.h16} aria-hidden />
              </InputAdornment>
            ),
            // Always present, so the slot's width is reserved; `visibility: hidden` takes the
            // button out of the accessibility tree and the tab order while keeping its box. The
            // resting field therefore LOOKS like it carries no ✕ and MEASURES as if it did —
            // which is the point: the field must not change width when the ✕ appears
            // (designer, 2026-10-05).
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  // Neutral grey (`text.secondary`), not the brand purple: the ✕ is a quiet
                  // utility beside the text, not an action competing with it (designer,
                  // 2026-10-05).
                  color="secondary"
                  aria-label={clearLabel}
                  tabIndex={showClear ? undefined : -1}
                  sx={{ visibility: showClear ? 'visible' : 'hidden' }}
                  // Keep focus in the input across a mouse click, so the caret never leaves and
                  // the label tab's focused state never flickers. Keyboard users who Tab to the
                  // ✕ and press it are focused back by `clear`.
                  onMouseDown={event => event.preventDefault()}
                  onClick={clear}
                >
                  <X size={sizes.height.h14} />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      {hint && (
        // The hint as the input's DESCRIPTION for assistive tech. The tooltip is visual: MUI
        // would describe the Root, not the input, and a description has to sit on the element
        // that has focus to be read.
        <span id={hintId} style={visuallyHidden} aria-live="polite">
          {belowThreshold ? hint : null}
        </span>
      )}
    </StyledRoot>
  );

  if (!hint) return field;

  return (
    <Tooltip title={hint} open={hintOpen} placement="bottom-start" disableInteractive describeChild>
      {field}
    </Tooltip>
  );
});
